import React, {useState, useEffect} from "react";
import {datadogRum} from "@datadog/browser-rum";
import FormOption from '../form-option';
import ModalDialog from '../modal-dialog';
import Loader from '../../components/loader';
import {
  generatePath,
  generateRedirectPathForOmniPartners,
  redirectNavigationUrl
} from '../../utilities/RedirectUtils';
import {rescueCancellation, sendJacadaRequest} from "../../api";
import {useLocale} from "../../contexts/LocaleContext";
import {TRANSLATION_CONSTANTS} from "../../app-consts/translations";
import {Button} from 'mesh-component-library';
import {PartnerDetails} from "../../types";
import { emitJourneyEvent, JOURNEY_STEP_TYPES } from "../../utils/journeyMonitoring";

export type CancellationModalProps = {
  incomingAppId: string;
  callId: string;
  source: string;
  startTime: string;
  serviceType: string; //Tow Tire Change Fuel Delivery Lockout
  partnerDetails: PartnerDetails;
  isSMSRescueTracker: boolean;
  history: any;
  ppuFlag: string;
  updatePartnerDetailsPartnerCode: () => void;
  updatePartnerDetailsDisplayCode: () => void;
  updatePartnerDetailsRoute: () => void;
  refreshReducersToInitial: () => void;
  providerId?: string;
  clearDuplicateRescue?: () => void;
  hideCancelModal?: () => void;
  setRescueCancelled?: (value: boolean) => void;
  setShowCancellationError?: (value: boolean) => void;
  useAlternateCancellationEndpoint?: boolean;
}
export const CancellationModal = ({
                                    incomingAppId,
                                    callId,
                                    clearDuplicateRescue = () => {},
                                    hideCancelModal = () => {},
                                    history,
                                    isSMSRescueTracker,
                                    partnerDetails,
                                    ppuFlag,
                                    providerId,
                                    refreshReducersToInitial,
                                    source,
                                    startTime,
                                    serviceType,
                                    setRescueCancelled = () => {},
                                    setShowCancellationError  = () => {},
                                    updatePartnerDetailsDisplayCode,
                                    updatePartnerDetailsPartnerCode,
                                    updatePartnerDetailsRoute,
                                    useAlternateCancellationEndpoint = false
                                  }: CancellationModalProps) => {
  const [cancelButtonDisabled, setCancelButtonDisabled] = useState(true)
  const [cancelReasonText, setCancelReasonText] = useState("")
  const getTranslatedText = useLocale()
  const [showSpinner, setShowSpinner] = useState(false)

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const emitCancellationEvent = (cancelResponse, partnerCode: string) => {
    emitJourneyEvent(JOURNEY_STEP_TYPES.USER_MILESTONE,
      cancelResponse?.success ? "rescue.cancelled" : "rescue.cancellation_failed", {
      milestoneName: cancelResponse?.success ? "rescue.cancelled" : "rescue.cancellation_failed",
      callId,
      partnerCode,
      serviceType,
    });
  };

  const cancelService = async () => {
    setShowSpinner(true)
    const {appId, displayCode, partnerCode} = partnerDetails;
    const values = new URLSearchParams(window.location.search);
    const aniValue = values.get('ANI')
    const sessionRoute = sessionStorage.getItem('route')?.toLocaleLowerCase();
    const cancellationAppId = incomingAppId || appId;
    const cancelRescueRequest = {
      cancelReason: cancelReasonText,
      providerId,
      requestedBy: source,
      serviceType,
      startTime
    }
    const cancelResponse = await rescueCancellation(
      cancellationAppId,
      displayCode,
      callId,
      cancelRescueRequest,
      partnerCode
    )

    emitCancellationEvent(cancelResponse, partnerCode);

    if (useAlternateCancellationEndpoint) {
      const redirectedSession = sessionStorage.getItem('invalid_session');
      hideCancelModal()
      if ((aniValue || sessionRoute === 'member' || sessionRoute === 'benefits' || sessionRoute === 'ppu' || redirectedSession) && isSMSRescueTracker) {
        redirectPageForOmniPartners(partnerDetails, cancelResponse, {
          ppuFlag,
          updatePartnerDetailsPartnerCode,
          updatePartnerDetailsDisplayCode,
          updatePartnerDetailsRoute,
          refreshReducersToInitial,
          history
        }, displayCode);
      } else if (cancelResponse?.success) {
        setShowCancellationError(false)
        setRescueCancelled(true)
        clearDuplicateRescue()
      } else {
        setShowCancellationError(true)
        datadogRum.addError(`${displayCode} Rescue cancellation resulted in error`, {
          status: cancelResponse?.data?.status || cancelResponse?.status,
          callId,
          cancelRescueRequest
        })
      }
    } else {
      handleOpenApiCancellationCleanup(cancelResponse)
    }
  }

  const handleOpenApiCancellationCleanup = (cancelResponse: any) => {
    let eventsData = [{
      'name': 'cancelReason',
      'value': cancelReasonText
    }];
    let jacadaRequestData = {
      'externalSystemId': localStorage.getItem('conversationId'),
      'events': eventsData
    }
    sendJacadaRequest(jacadaRequestData);

    let url = redirectNavigationUrl(partnerDetails);
    if (cancelResponse?.success) {
      localStorage.clear()
      localStorage.setItem('rescueCancel', 'YES');
      localStorage.setItem('isPpu', ppuFlag === 'Y' ? 'true' : 'false')
    } else {
      url = 'sitedown';
    }
    refreshReducersToInitial();
    history?.push(generatePath(url));
  }

  const enableButtonCancelReason = (event: { currentTarget: { defaultValue: React.SetStateAction<string>; }; }) => {
    setCancelReasonText(event.currentTarget.defaultValue);
    setCancelButtonDisabled(false);
  }

  const displaySpinner = () => {
    return showSpinner ?
      <div className="c-overlay has-loader is-fixed">
        <Loader className="c-loader--xl c-loader--position"/>
      </div>
      :
      null
  }

  return (
    <div className='cancel-rescue-modal'>
      {displaySpinner()}
      <ModalDialog
        id="cancellationModal"
        title={getTranslatedText(TRANSLATION_CONSTANTS.CANCEL_RESCUE_CONFIRMATION_HEADER)}
        hideTrigger
        showCloseBtn={true}
        isActive={true}
        onClose={hideCancelModal}
        footer={
          <Button
            size='lg'
            hasUpgrade
            id="cancel-button"
            disabled={cancelButtonDisabled}
            onClick={cancelService}
            utils={{
              fullWidth: true
            }}>
            {getTranslatedText(TRANSLATION_CONSTANTS.CANCEL_RESCUE_BUTTON_TEXT)}
          </Button>
        }>
        <div className="c-fuelType" data-dd-privacy="allow">
          <div className="heading-2">
            <label>{getTranslatedText(TRANSLATION_CONSTANTS.CANCEL_RESCUE_AFTER_CONFIRMATION_BODY)}</label>
          </div>
          <FormOption
            onClick={enableButtonCancelReason}
            onKeyDown={enableButtonCancelReason}
            label={getTranslatedText(TRANSLATION_CONSTANTS.CANCEL_RESCUE_AFTER_CONFIRMATION_OPTION_TEXT1)}
            value="Service no longer needed"
            name="cancelReason"
            type="radio"/>
          <br/>
          <FormOption
            onClick={enableButtonCancelReason}
            onKeyDown={enableButtonCancelReason}
            label={getTranslatedText(TRANSLATION_CONSTANTS.CANCEL_RESCUE_AFTER_CONFIRMATION_OPTION_TEXT2)}
            value="Unacceptable ETA"
            name="cancelReason"
            type="radio"/>
          <br/>
          <FormOption
            onClick={enableButtonCancelReason}
            onKeyDown={enableButtonCancelReason}
            label={getTranslatedText(TRANSLATION_CONSTANTS.CANCEL_RESCUE_AFTER_CONFIRMATION_OPTION_TEXT3)}
            value="Other"
            name="cancelReason"
            type="radio"/>
        </div>
      </ModalDialog>
    </div>
  )
}

type RedirectParams = {
  ppuFlag: string;
  history: any;
  refreshReducersToInitial: () => void;
  updatePartnerDetailsPartnerCode: (partnerCode: string) => void;
  updatePartnerDetailsDisplayCode: (displayCode: string) => void;
  updatePartnerDetailsRoute: (route: string) => void;
}
export const redirectPageForOmniPartners = (
  partnerDetails,
  cancelResponse,
  props: RedirectParams,
  displayCode) => {
  let url = redirectNavigationUrl(partnerDetails, partnerDetails?.route, displayCode);
  if (cancelResponse?.success) {
    let tollFreeNumber = localStorage.getItem('tollFreeNumber');
    localStorage.clear();
    localStorage.setItem('tollFreeNumber', tollFreeNumber);
    localStorage.setItem('rescueCancel', 'YES');
    localStorage.setItem('isPpu', props.ppuFlag === 'Y' ? 'true' : 'false');
  } else {
    url = 'sitedown';
  }
  props.refreshReducersToInitial();
  props.updatePartnerDetailsPartnerCode(partnerDetails?.partnerCode);
  props.updatePartnerDetailsDisplayCode(displayCode);
  props.updatePartnerDetailsRoute(partnerDetails?.route);
  props.history.push(generateRedirectPathForOmniPartners(partnerDetails, displayCode, url));
};
